import React, {Component} from 'react';
import {Link, Navigate} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {ArrowLeft} from '../../icons';
import {LayoutSidebar} from '../../../Layout';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {AccessService} from '../../../utils/AccessService';
import {useFicha21} from '../../../context/UserModulesContext';
import {withRouterParams} from '../../../utils/withRouterParams';
import {ClientHeader} from '../ClientHeader';
import {ClientTabs} from '../ClientTabs';
import {MediaPlanConnectionsView} from './MediaPlanConnectionsView';
import {MediaPlanProposalView} from './MediaPlanProposalView';

// Tab "Media plan" del cliente (Media Plan Sync F0). Ruta `/client/:id/media-plan`.
// La agencia conecta acá la planilla del media plan (Google Sheets); en F1 esta pantalla suma la
// propuesta de cambios (crear/actualizar Líneas de Control desde el plan, con revisión).
class ClientMediaPlanTabContentClass extends Component {
    constructor(props) {
        super(props);
        // `tabPickerOpen` vive acá porque el selector de solapas lo abren DOS superficies: el chip
        // del strip y el CTA del empty state de la propuesta. `tabScan` viaja en sentido inverso al
        // viejo `sourceInfo`: es propiedad de la conexión, no de la propuesta — por eso ahora se ve
        // incluso cuando no hay propuesta, que es justo cuando hace falta.
        // `tabPickerNonce` sube en cada pedido de preselección: el gid solo no alcanza para que el
        // selector reaccione cuando se pide DOS VECES la misma candidata.
        this.state = {name: '', activeConnection: null, firstReadRunning: false, sourceInfo: null,
                      tabPickerOpen: null, tabPickerPreselect: null, tabPickerNonce: 0,
                      tabScan: null, proposalSummary: null};
    }

    // Resumen de la propuesta para el encabezado de tarea (2.1): pendientes + vinculables. Se
    // compara por campos porque el objeto se re-crea en cada efecto (mismo criterio que
    // `handleSnapshotInfo`); sin esto el setState re-dispara el efecto y se cicla.
    handleProposalSummaryChanged = (summary) => {
        const current = this.state.proposalSummary;
        const same = (current === summary) || (current && summary
            && current.pending === summary.pending && current.linkable === summary.linkable);
        if (!same) this.setState({proposalSummary: summary});
    };

    // "Vincular líneas existentes" aterriza donde se decide: el bulk de sugeridas si está, o la
    // lista de cambios (las cards de CREATE con candidatas viven ahí). Los selectores son de ESTA
    // misma pantalla (`MediaPlanProposalView`); si esas clases se renombran, esto se renombra con
    // ellas.
    scrollToLinkCards = () => {
        const target = document.querySelector('.mp-bulk-link')
            || document.querySelector('.mp-change-list');
        if (target) target.scrollIntoView({behavior: 'smooth', block: 'start'});
    };

    handleTabScanChanged = (scan) => {
        if (JSON.stringify(scan) !== JSON.stringify(this.state.tabScan)) this.setState({tabScan: scan});
    };

    handlePickerOpenChange = (connectionId) => {
        if (connectionId !== this.state.tabPickerOpen) {
            this.setState({tabPickerOpen: connectionId, tabPickerPreselect: null});
        }
    };

    // `gid` opcional: cuando el empty state ofrece una candidata concreta ("usar esta solapa"), el
    // selector se abre con ESA marcada y el usuario solo confirma. Fijar una solapa relee el plan y
    // marca STALE la propuesta abierta, así que el paso de confirmación se mantiene a propósito.
    openTabPicker = (gid = null) => {
        const active = this.state.activeConnection;
        if (!active) return;
        this.setState((state) => ({
            tabPickerOpen: active.id,
            tabPickerPreselect: gid,
            tabPickerNonce: state.tabPickerNonce + 1,
        }));
    };

    handleConnectionsChanged = (connections) => {
        const active = (connections || []).find((c) => c.status === 'ACTIVE') || null;
        this.setState({activeConnection: active});
    };

    handleFirstReadRunningChanged = (running) => {
        if (running !== this.state.firstReadRunning) this.setState({firstReadRunning: running});
    };

    // La propuesta reporta cuándo fue el último cambio de contenido detectado (`readAt`), que el
    // strip muestra junto a "última verificación". La SOLAPA ya no viene por acá: es propiedad de
    // la conexión (`tab_scan`). Comparación por campos (el objeto se re-crea en cada efecto).
    handleSnapshotInfo = (info) => {
        const current = this.state.sourceInfo;
        const same = (current === info) || (current && info && current.readAt === info.readAt);
        if (!same) this.setState({sourceInfo: info});
    };

    componentDidMount() {
        this.getClientData();
    }

    getClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({name: data.data['name']});
            }
        });
    }

    /**
     * Encabezado de tarea (sólo 2.1): la vuelta al Panel + qué es esta pantalla + cuánto pide
     * revisión. La pill y el botón salen del resumen que reporta la propuesta
     * (`onSummaryChanged`): la pill usa la MISMA definición que `media_plan.pending_items` del
     * agregado del Panel — si contaran distinto, la card del Panel y esta pantalla dirían dos
     * números para lo mismo. El botón de vincular sólo aparece cuando hay cards que ofrecen esa
     * decisión (un botón que no hace nada no es un botón).
     *
     * El plan conectado y la solapa NO se repiten acá: viven en el source strip, veinte píxeles
     * más abajo. El subtítulo dice de qué cliente es la tarea, que es lo que la vuelta promete.
     */
    renderTaskHeader() {
        const {t} = this.props;
        const summary = this.state.proposalSummary;
        return (
            <div className="mp-task">
                <Link className="mp-task__back" to={`/client/${this.props.clientId}`}>
                    <ArrowLeft size={14} weight="bold"/>
                    {this.state.name
                        ? t('media_plan.task_header.back', 'Volver al panel de {{client}}',
                            {client: this.state.name, interpolation: {escapeValue: false}})
                        : t('media_plan.task_header.back_generic', 'Volver al panel')}
                </Link>
                <div className="mp-task__bar">
                    <div className="mp-task__txt">
                        <h1 className="mp-task__title">{t('media_plan.task_header.title', 'Media plan')}</h1>
                        {this.state.name && <div className="mp-task__sub">{this.state.name}</div>}
                    </div>
                    {summary && summary.pending > 0 && (
                        <span className="mp-task__pill">
                            {t('media_plan.task_header.pending', {
                                count: summary.pending,
                                defaultValue: '{{count}} propuesta por revisar',
                                defaultValue_other: '{{count}} propuestas por revisar',
                            })}
                        </span>
                    )}
                    {summary && summary.linkable > 0 && (
                        <button type="button" className="mp-task__btn" onClick={this.scrollToLinkCards}>
                            {t('media_plan.task_header.link_existing', 'Vincular líneas existentes')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    render() {
        // Guard ante URL directa: el Media Plan es capability del tier IA, así que sobrevive al
        // switch al clásico (antes el guard era `ficha_v2`, o sea el rediseño, un tier más arriba
        // de lo que corresponde). Mientras el provider no cargó se espera, para no redirigir en
        // falso por un flag que todavía no llegó. El backend gatea igual por su lado.
        //
        // El módulo MCP se chequea acá TAMBIÉN porque el tab lo exige (`ClientTabs.hasMediaPlanTab`):
        // sin esto, quien no ve el tab igual entra por URL directa y los dos gates dicen cosas
        // distintas. Backend gatea sólo por tier — si algún día el Media Plan deja de depender del
        // módulo MCP, hay que sacarlo de los DOS lugares a la vez.
        if (!this.props.modulesLoaded) return null;
        if (!this.props.mediaPlanEnabled || !this.props.hasMcpModule) {
            return <Navigate to={`/client/${this.props.clientId}/monitoring`} replace/>;
        }
        return (
            <section className="section-content">
                {/* En 2.1 el media plan se lee como una TAREA a la que se entra desde el Panel
                    (card gtAL5axI, paso 5): el encabezado de tarea reemplaza al ClientHeader (el
                    nombre del cliente ya vive en la vuelta y en el subtítulo) y va DESPUÉS de los
                    tabs, como el de la vista de líneas. En clásico la pantalla queda byte por byte
                    como estaba. */}
                {!this.props.ficha21 && <ClientHeader name={this.state.name}/>}
                <ClientTabs clientId={this.props.clientId}/>
                {this.props.ficha21 && this.renderTaskHeader()}

                <div className="section-body">
                    {/* Panel único: la conexión de la planilla (source strip) es el encabezado y la
                        propuesta el cuerpo — sin dos `.box` apilados ni costura entre bloques. */}
                    <div className="box mp-panel">
                        <MediaPlanConnectionsView
                            clientId={this.props.clientId}
                            onConnectionsChanged={this.handleConnectionsChanged}
                            onFirstReadRunningChanged={this.handleFirstReadRunningChanged}
                            sourceInfo={this.state.sourceInfo}
                            pickerOpenFor={this.state.tabPickerOpen}
                            pickerPreselectGid={this.state.tabPickerPreselect}
                            pickerPreselectNonce={this.state.tabPickerNonce}
                            onPickerOpenChange={this.handlePickerOpenChange}
                            onTabScanChanged={this.handleTabScanChanged}
                        />
                        {/* `key` por conexión: lo elegido en el reparto o en la base es de ESA planilla y
                            no tiene que sobrevivir a un cambio de conexión. */}
                        {this.state.activeConnection && (
                            <MediaPlanProposalView
                                key={this.state.activeConnection.id}
                                clientId={this.props.clientId}
                                connection={this.state.activeConnection}
                                firstReadRunning={this.state.firstReadRunning}
                                onSnapshotInfo={this.handleSnapshotInfo}
                                onSummaryChanged={this.handleProposalSummaryChanged}
                                tabScan={this.state.tabScan}
                                onPickTab={this.openTabPicker}
                            />
                        )}
                    </div>
                </div>
            </section>
        );
    }
}

const ClientMediaPlanTabContentInner = withTranslation()(ClientMediaPlanTabContentClass);

// El guard sale del provider (fuente única), no de un fetch propio de esta pantalla. `ficha21`
// gobierna sólo el encabezado de tarea (paso 5 de gtAL5axI) — el gate de ACCESO sigue siendo la
// capability (mediaPlanEnabled + MCP), que sobrevive al switch al clásico.
function ClientMediaPlanTabContent({clientId}) {
    const {mediaPlanEnabled, modules, loaded, ficha21} = useFicha21();
    return <ClientMediaPlanTabContentInner clientId={clientId}
                                           mediaPlanEnabled={mediaPlanEnabled}
                                           hasMcpModule={(modules || []).includes(AccessService.MODULE_DASHBO_MCP)}
                                           modulesLoaded={loaded}
                                           ficha21={ficha21}/>;
}

export class ClientMediaPlanTab extends Component {
    render() {
        return (
            <div className="app-content">
                <LayoutSidebar/>
                <ClientMediaPlanTabContent clientId={this.props.match.params.id}/>
            </div>
        );
    }
}

export default withRouterParams(ClientMediaPlanTab);
